import { Container, Row, Col, Spinner, Badge } from 'react-bootstrap';
import { FaHandshake } from 'react-icons/fa';
import { useAboutPage, getAboutSectionByKey, iconMap } from '@/hooks/useAboutPage';

const Partnerships = ({ sectionData: propSectionData }) => {
  const { sections, loading } = useAboutPage();
  const partnershipsSection = propSectionData || getAboutSectionByKey(sections, 'partnerships');
  
  const bgColor = partnershipsSection?.background_color || 'light';
  const bgEffect = partnershipsSection?.background_effect || null;
  let bgClass = bgColor === 'primary' ? 'section-bg-primary' : bgColor === 'white' ? 'bg-white' : 'bg-light';
  // Apply gradient effect if selected
  if (bgColor === 'primary' && bgEffect === 'gradient') {
    bgClass = 'section-bg-primary-gradient';
  }
  
  if (loading && !propSectionData) {
    return (
      <section className={`py-4 py-lg-5 ${bgClass} about-section partnerships-section`}>
        <Container>
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
          </div>
        </Container>
      </section>
    );
  }
  
  const listItems = partnershipsSection?.items || [];
  
  if (!partnershipsSection || listItems.length === 0) {
    return null;
  }
  
  const description = partnershipsSection.content?.description || '';
  const descriptionIcon = partnershipsSection.content?.description_icon || '';
  const descriptionTitle = partnershipsSection.content?.description_title || 'Trusted Partnerships';
  const descriptionSubtitle = partnershipsSection.content?.description_subtitle || 'Over 40 years of excellence';
  const hasDotsEffect = bgColor === 'primary' && bgEffect === 'dots';

  // Helper to get icon component from icon string
  const getIconFromString = (iconString) => {
    if (!iconString || !iconMap) return null;
    return iconMap[iconString] || null;
  };

  // Get description icon component (use selected icon or fallback to FaHandshake)
  const DescriptionIcon = descriptionIcon ? getIconFromString(descriptionIcon) : FaHandshake;

  // Separate clients and partnerships items
  // Support both old structure (bullets in content) and new structure (individual items)
  const clientsHeaderItem = listItems.find(item => item.title?.toLowerCase().includes('client'));
  const partnershipsHeaderItem = listItems.find(item => item.title?.toLowerCase().includes('partnership'));
  
  // Get items that belong to clients or partnerships
  // Items with bullets (old structure) or individual items (new structure)
  let clientsItems = [];
  let partnershipsItems = [];

  // Check if using old structure (bullets in header items)
  if (clientsHeaderItem?.content?.bullets && clientsHeaderItem.content.bullets.length > 0) {
    // Check if bullets are objects (with icons) or strings (old format)
    const firstBullet = clientsHeaderItem.content.bullets[0];
    if (firstBullet && typeof firstBullet === 'object' && firstBullet.text) {
      // New format: bullets with icons
      clientsItems = clientsHeaderItem.content.bullets.map((bullet, idx) => ({
        id: `client-bullet-${idx}`,
        title: bullet.text || '',
        IconComponent: getIconFromString(bullet.icon),
        icon_color: bullet.icon_color || 'primary'
      }));
    } else {
      // Old format: simple strings
      clientsItems = clientsHeaderItem.content.bullets.map((bullet, idx) => ({
        id: `client-bullet-${idx}`,
        title: typeof bullet === 'string' ? bullet : bullet.text || '',
        IconComponent: null, // Will use fallback
        icon_color: 'primary'
      }));
    }
  } else {
    // New structure: use individual items (excluding header items)
    clientsItems = listItems.filter(item => 
      item.id !== clientsHeaderItem?.id && 
      !item.title?.toLowerCase().includes('partnership') &&
      item.item_type === 'bullet'
    );
  }
  
  if (partnershipsHeaderItem?.content?.bullets && partnershipsHeaderItem.content.bullets.length > 0) {
    // Check if bullets are objects (with icons) or strings (old format)
    const firstBullet = partnershipsHeaderItem.content.bullets[0];
    if (firstBullet && typeof firstBullet === 'object' && firstBullet.text) {
      // New format: bullets with icons
      partnershipsItems = partnershipsHeaderItem.content.bullets.map((bullet, idx) => ({
        id: `partnership-bullet-${idx}`,
        title: bullet.text || '',
        IconComponent: getIconFromString(bullet.icon),
        icon_color: bullet.icon_color || 'primary'
      }));
    } else {
      // Old format: simple strings
      partnershipsItems = partnershipsHeaderItem.content.bullets.map((bullet, idx) => ({
        id: `partnership-bullet-${idx}`,
        title: typeof bullet === 'string' ? bullet : bullet.text || '',
        IconComponent: null, // Will use fallback
        icon_color: 'primary'
      }));
    }
  } else {
    // New structure: use individual items (excluding header items)
    partnershipsItems = listItems.filter(item => 
      item.id !== partnershipsHeaderItem?.id && 
      !item.title?.toLowerCase().includes('client') &&
      item.item_type === 'bullet'
    );
  }

  
  return (
    <section className={`py-4 py-lg-5 ${bgClass} about-section partnerships-section ${hasDotsEffect ? 'position-relative overflow-hidden' : ''}`}>
      {hasDotsEffect && (
        <>
          <span className="section-bg-dots section-bg-dots--left d-none d-xl-block" />
          <span className="section-bg-dots section-bg-dots--right d-none d-lg-block" />
        </>
      )}
      <Container className={hasDotsEffect ? 'position-relative' : ''}>
        {/* Header Section */}
        <Row className="mb-4">
          <Col>
            {partnershipsSection.subtitle && (
              <Badge bg="primary" className="bg-opacity-10 text-primary mb-3 px-3 py-2">
                {partnershipsSection.subtitle}
              </Badge>
            )}
            {partnershipsSection.title && (
              <h2 className="sec-title mb-3">{partnershipsSection.title}</h2>
            )}
          </Col>
        </Row>

        {/* Description Card - Full Width, Left Aligned */}
        {description && (
          <Row className="mb-4">
            <Col>
              <div className="partnerships-description-card">
                <div className="d-flex align-items-center gap-3 mb-3">
                  {DescriptionIcon && (
                    <div className="partnerships-icon-wrapper">
                      <DescriptionIcon className="text-primary" size={32} />
                    </div>
                  )}
                  <div>
                    {descriptionTitle && (
                      <h5 className="mb-0 fw-bold" style={{ color: '#000000' }}>{descriptionTitle}</h5>
                    )}
                    {descriptionSubtitle && (
                      <p className="mb-0 small text-muted">{descriptionSubtitle}</p>
                    )}
                  </div>
                </div>
                <p className="intro-text mb-0" dangerouslySetInnerHTML={{ __html: description }} />
              </div>
            </Col>
          </Row>
        )}

        {/* Clients Section */}
        {clientsItems.length > 0 && (
          <div className="partnerships-subsection mb-5">
            <Row className="mb-4">
              <Col>
                <div className="d-flex align-items-center gap-3 mb-3">
                  <div className="partnerships-section-divider partnerships-section-divider--clients"></div>
                  {clientsHeaderItem?.title && (
                    <h3 className="h5 fw-bold mb-0" style={{ color: '#000000' }}>
                      {clientsHeaderItem.title}
                    </h3>
                  )}
                </div>
              </Col>
            </Row>
            <Row className="g-4">
              {clientsItems.map((item, idx) => {
                // Only use icon if explicitly set in database
                const Icon = item.IconComponent || null;
                const iconColor = item.icon_color || 'primary';
                const displayText = item.title || item;
                return (
                  <Col key={item.id || idx} md={6} lg={3}>
                    <div className="partnerships-item-box partnerships-item-box--clients h-100">
                      <div className="d-flex flex-column align-items-center text-center">
                        {Icon && (
                          <div className={`partnerships-item-icon mb-3 bg-${iconColor} bg-opacity-10 text-${iconColor}`}>
                            <Icon size={24} />
                          </div>
                        )}
                        <p className="mb-0 fw-semibold" style={{ color: '#000000', lineHeight: '1.5' }}>
                          {displayText}
                        </p>
                      </div>
                    </div>
                  </Col>
                );
              })}
            </Row>
          </div>
        )}

        {/* Partnerships Section */}
        {partnershipsItems.length > 0 && (
          <div className="partnerships-subsection">
            <Row className="mb-4">
              <Col>
                <div className="d-flex align-items-center gap-3 mb-3">
                  <div className="partnerships-section-divider partnerships-section-divider--partnerships"></div>
                  {partnershipsHeaderItem?.title && (
                    <h3 className="h5 fw-bold mb-0" style={{ color: '#000000' }}>
                      {partnershipsHeaderItem.title}
                    </h3>
                  )}
                </div>
              </Col>
            </Row>
            <Row className="g-4">
              {partnershipsItems.map((item, idx) => {
                // Only use icon if explicitly set in database
                const Icon = item.IconComponent || null;
                const iconColor = item.icon_color || 'primary';
                const displayText = item.title || item;
                return (
                  <Col key={item.id || idx} md={6} lg={3}>
                    <div className="partnerships-item-box partnerships-item-box--partnerships h-100">
                      <div className="d-flex flex-column align-items-center text-center">
                        {Icon && (
                          <div className={`partnerships-item-icon mb-3 bg-${iconColor} bg-opacity-10 text-${iconColor}`}>
                            <Icon size={24} />
                          </div>
                        )}
                        <p className="mb-0 fw-semibold" style={{ color: '#000000', lineHeight: '1.5' }}>
                          {displayText}
                        </p>
                      </div>
                    </div>
                  </Col>
                );
              })}
            </Row>
          </div>
        )}
      </Container>
    </section>
  );
};

export default Partnerships;

